import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RiskBadge, StageBadge, StatusBadge, EmptyState, PriorityBadge } from "@/components/common/Bits";
import { fmtDate, fmtDateTime, DOC_TYPES, METRIC_TYPES, REEVAL_TRIGGERS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, ShieldCheck, Send, RefreshCcw, Pencil, Plus, FileText, History, Radar, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const Meta = ({ label, value }) => (
  <div>
    <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">{label}</p>
    <p className="text-sm mt-0.5 font-medium">{value ?? "—"}</p>
  </div>
);

export default function InitiativeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [d, setD] = useState(null);
  const [catalogs, setCatalogs] = useState(null);
  const [dossier, setDossier] = useState(null);

  const load = useCallback(async () => {
    const [{ data }, { data: check }] = await Promise.all([
      api.get(`/initiatives/${id}`),
      api.get(`/initiatives/${id}/dossier-check`),
    ]);
    setD(data);
    setDossier(check);
  }, [id]);

  useEffect(() => {
    load().catch(() => toast.error("No fue posible cargar la iniciativa"));
    api.get("/catalogs").then(({ data }) => setCatalogs(data));
  }, [load]);

  if (!d) return <div className="space-y-4"><Skeleton className="h-24 rounded-xl" /><Skeleton className="h-96 rounded-xl" /></div>;

  const ini = d.initiative;

  const submitCommittee = async () => {
    try {
      await api.post(`/initiatives/${id}/submit`);
      toast.success("Iniciativa enviada al Comité de IA");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-muted-foreground -ml-2" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4 mr-1" /> Volver
      </Button>

      {/* Header */}
      <div className="bg-card rounded-xl border border-border shadow-card p-6 sm:p-8 animate-in-up" data-testid="initiative-header">
        <div className="flex flex-col lg:flex-row lg:items-start gap-6">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span className="font-mono">{ini.code}</span> · <span>{ini.asset_type}</span> · <span>{ini.department}</span>
              {ini.from_shadow_ai && (
                <Badge variant="outline" className="bg-risk-medhigh/10 text-risk-medhigh border-risk-medhigh/25">
                  <Radar className="h-3 w-3 mr-1" /> Origen Shadow AI
                </Badge>
              )}
            </div>
            <h1 className="font-display text-2xl sm:text-3xl font-semibold mt-2 leading-tight">{ini.name}</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-3xl leading-relaxed">{ini.description}</p>
            <div className="flex flex-wrap gap-2 mt-4">
              <StageBadge stage={ini.stage} />
              <RiskBadge level={ini.residual_risk} />
              <StatusBadge status={ini.committee_status} />
              <StatusBadge status={ini.evaluation_status} />
            </div>
          </div>
          {can.edit && (
            <div className="flex flex-wrap gap-2 shrink-0">
              <Button asChild variant="outline" className="border-border" data-testid="evaluate-btn">
                <Link to={`/iniciativas/${id}/evaluar`}><ShieldCheck className="h-4 w-4 mr-2" /> Evaluar riesgo</Link>
              </Button>
              <Button onClick={submitCommittee} disabled={!dossier?.complete || ini.committee_status === "En revisión"} className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="submit-committee-btn">
                <Send className="h-4 w-4 mr-2" /> Enviar al Comité
              </Button>
              <EditDialog ini={ini} catalogs={catalogs} onSaved={load} />
              <ReevalDialog ini={ini} onSaved={load} />
            </div>
          )}
        </div>

        {dossier && !dossier.complete && (
          <div className="mt-5 rounded-lg border border-risk-medium/30 bg-risk-medium/5 px-4 py-3" data-testid="dossier-warning">
            <p className="text-xs font-semibold text-risk-medium">Dossier incompleto para decisión del Comité</p>
            <ul className="text-xs text-muted-foreground mt-1 space-y-0.5">{dossier.missing.map((m) => <li key={m}>· {m}</li>)}</ul>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-6 gap-y-4 mt-6 pt-6 border-t border-border">
          <Meta label="Business owner" value={ini.business_owner} />
          <Meta label="Resp. técnico" value={ini.tech_owner} />
          <Meta label="Resp. clínico" value={ini.clinical_owner} />
          <Meta label="Proveedor" value={ini.provider} />
          <Meta label="Modelo" value={`${ini.model} · v${ini.model_version}`} />
          <Meta label="Región de procesamiento" value={ini.processing_region} />
          <Meta label="Clasificación" value={ini.data_classification} />
          <Meta label="Datos personales" value={ini.personal_data ? "Sí" : "No"} />
          <Meta label="Datos clínicos" value={ini.clinical_data ? "Sí" : "No"} />
          <Meta label="Usuarios estimados" value={ini.estimated_users} />
          <Meta label="Volumen" value={ini.usage_volume} />
          <Meta label="Valor esperado" value={`${ini.expected_value} · ${ini.roi_estimate || "—"}`} />
          <Meta label="Próxima acción" value={ini.next_action} />
          <Meta label="Última evaluación" value={fmtDate(ini.last_assessment_date)} />
          <Meta label="Próxima revisión" value={fmtDate(ini.next_review_date)} />
          <Meta label="Integraciones" value={(ini.integrations || []).join(", ") || "—"} />
          <Meta label="Creación" value={fmtDate(ini.created_at)} />
          <Meta label="Última actualización" value={fmtDate(ini.updated_at)} />
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="evaluaciones">
        <TabsList className="bg-muted flex-wrap h-auto">
          {[["evaluaciones", `Evaluaciones (${d.assessments.length})`], ["controles", `Controles (${d.controls.length})`], ["evidencias", `Evidencias (${d.documents.length})`], ["decisiones", `Decisiones (${d.decisions.length})`], ["metricas", `Métricas (${d.metrics.length})`], ["historial", "Historial"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="text-xs sm:text-sm">{l}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="evaluaciones" className="mt-5 space-y-4">
          {d.assessments.length === 0 && <EmptyState icon={ShieldCheck} title="Sin evaluaciones" subtitle="Ejecute la primera evaluación de riesgo para esta iniciativa." />}
          {d.assessments.map((a) => (
            <Card key={a.id} className="shadow-card border-border">
              <CardContent className="p-5">
                <div className="flex flex-wrap items-center gap-3 justify-between">
                  <div>
                    <p className="font-display font-semibold text-sm">Versión {a.version} · {fmtDate(a.date)}</p>
                    <p className="text-xs text-muted-foreground">Evaluador: {a.evaluator}</p>
                  </div>
                  <div className="flex gap-2 items-center">
                    <span className="text-xs text-muted-foreground">Inherente</span><RiskBadge level={a.inherent_level} />
                    <span className="text-xs text-muted-foreground">Residual</span><RiskBadge level={a.residual_level} />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4 mt-4 text-xs">
                  <div>
                    <p className="font-semibold uppercase tracking-wider text-muted-foreground">Controles requeridos</p>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">{a.required_controls.map((c) => <span key={c} className="px-2 py-0.5 rounded bg-secondary text-secondary-foreground">{c}</span>)}</div>
                  </div>
                  <div className="space-y-1.5">
                    <p className="font-semibold uppercase tracking-wider text-muted-foreground">Revisiones requeridas</p>
                    <div className="flex flex-wrap gap-2">
                      {[["dpo", "DPO"], ["clinical", "Clínica"], ["security", "Seguridad"], ["committee", "Comité de IA"]].map(([k, l]) => (
                        <span key={k} className="flex items-center gap-1">
                          {a.flags?.[k] ? <CheckCircle2 className="h-3.5 w-3.5 text-success" /> : <XCircle className="h-3.5 w-3.5 text-muted-foreground/50" />}{l}
                        </span>
                      ))}
                    </div>
                    {a.diff_from_previous && <p className="text-muted-foreground mt-1">Diferencias vs versión anterior: {a.diff_from_previous}</p>}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="controles" className="mt-5">
          <div className="flex justify-end mb-3">{can.edit && catalogs && <AssignControlDialog id={id} catalogs={catalogs} existing={d.controls} onSaved={load} />}</div>
          {d.controls.length === 0 ? <EmptyState icon={ShieldCheck} title="Sin controles asignados" /> : (
            <div className="bg-card rounded-xl border border-border shadow-card overflow-x-auto">
              <Table>
                <TableHeader><TableRow className="hover:bg-transparent">{["Control", "Categoría", "Responsable", "Estado", "Fecha límite", "Validación", ""].map((h) => <TableHead key={h} className="text-xs uppercase tracking-wide whitespace-nowrap">{h}</TableHead>)}</TableRow></TableHeader>
                <TableBody>
                  {d.controls.map((c) => (
                    <TableRow key={c.id} data-testid={`control-row-${c.control_id}`}>
                      <TableCell className="font-medium text-sm">{c.control_name}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{c.category}</TableCell>
                      <TableCell className="text-sm">{c.owner}</TableCell>
                      <TableCell><StatusBadge status={c.status} /></TableCell>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(c.due_date)}</TableCell>
                      <TableCell className="text-xs">{c.validation_result || "—"}</TableCell>
                      <TableCell>
                        {can.edit && c.status !== "Implementado" && (
                          <Button size="sm" variant="outline" className="h-7 text-xs border-border" data-testid={`implement-control-${c.control_id}`}
                            onClick={async () => {
                              try {
                                await api.put(`/initiative-controls/${c.id}`, { status: "Implementado", evidence: "Evidencia registrada desde la ficha", validation_result: "Conforme" });
                                toast.success("Control marcado como implementado");
                                load();
                              } catch (e) { toast.error(apiError(e)); }
                            }}>
                            Marcar implementado
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="evidencias" className="mt-5">
          <div className="flex justify-end mb-3">{can.edit && <AddDocumentDialog id={id} onSaved={load} />}</div>
          {d.documents.length === 0 ? <EmptyState icon={FileText} title="Sin documentos" subtitle="Adjunte evidencias para completar el dossier." /> : (
            <div className="grid sm:grid-cols-2 gap-3">
              {d.documents.map((doc) => (
                <Card key={doc.id} className="shadow-card border-border">
                  <CardContent className="p-4 flex items-start gap-3">
                    <div className="h-9 w-9 rounded-lg bg-primary/8 text-primary flex items-center justify-center shrink-0"><FileText className="h-4 w-4" /></div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium leading-snug">{doc.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{doc.type} · v{doc.version} · {doc.owner} · {fmtDate(doc.date)}</p>
                      {doc.used_in_decision && <p className="text-[10px] text-accent mt-1">Utilizado en decisión · protegido contra eliminación</p>}
                    </div>
                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                      <StatusBadge status={doc.status} />
                      {can.edit && doc.status === "En revisión" && (
                        <Button size="sm" variant="ghost" className="h-6 text-[11px] text-accent" onClick={async () => { await api.put(`/documents/${doc.id}`, { status: "Aprobado" }); toast.success("Documento aprobado"); load(); }} data-testid={`approve-doc-${doc.id}`}>
                          Aprobar
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="decisiones" className="mt-5 space-y-4">
          {d.decisions.length === 0 && <EmptyState icon={History} title="Sin decisiones registradas" subtitle="Envíe la iniciativa al Comité cuando el dossier esté completo." />}
          {d.decisions.map((dec) => (
            <Card key={dec.id} className="shadow-card border-border">
              <CardContent className="p-5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <StatusBadge status={dec.status} />
                  <p className="text-xs text-muted-foreground">{dec.decided_at ? `Decidida el ${fmtDate(dec.decided_at)}` : `En votación · quórum ${dec.votes.length}/${dec.quorum_required}`}</p>
                </div>
                {dec.conditions?.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Condiciones</p>
                    <ul className="text-xs mt-1 space-y-1">{dec.conditions.map((c) => <li key={c} className="flex gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-risk-medium shrink-0 mt-0.5" />{c}</li>)}</ul>
                  </div>
                )}
                <div className="mt-4 space-y-2">
                  {dec.votes.map((v) => (
                    <div key={v.id} className="rounded-lg border border-border p-3">
                      <div className="flex flex-wrap justify-between gap-2">
                        <p className="text-xs font-semibold">{v.voter} <span className="text-muted-foreground font-normal">· {v.role}</span></p>
                        <StatusBadge status={v.vote.includes("Aprobar") ? (v.vote === "Aprobar" ? "Aprobada" : "Aprobada con condiciones") : v.vote === "Rechazar" ? "Rechazada" : "En revisión"} />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{v.justification}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="metricas" className="mt-5">
          <div className="flex justify-end mb-3">{can.edit && <AddMetricDialog id={id} onSaved={load} />}</div>
          {d.metrics.length === 0 ? <EmptyState icon={History} title="Sin métricas de valor" subtitle="Registre resultados reales para comparar con el valor esperado." /> : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {d.metrics.map((m) => (
                <Card key={m.id} className="shadow-card border-border">
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">{m.metric} · {m.period}</p>
                    <p className="font-display text-2xl font-semibold mt-1 tabular">{m.value} <span className="text-sm text-muted-foreground font-normal">{m.unit}</span></p>
                    <p className="text-[11px] text-muted-foreground mt-2">Meta: {m.target ?? "—"} · Anterior: {m.previous ?? "—"} · Fuente: {m.source}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="historial" className="mt-5 space-y-3">
          {d.shadow_origin && (
            <Card className="shadow-card border-risk-medhigh/30 bg-risk-medhigh/5">
              <CardContent className="p-4 flex items-start gap-3">
                <Radar className="h-4 w-4 text-risk-medhigh mt-0.5" />
                <div className="text-xs">
                  <p className="font-semibold">Trazabilidad de origen · Shadow AI</p>
                  <p className="text-muted-foreground mt-1">Detección: {d.shadow_origin.app_name} · Fuente: {d.shadow_origin.source} · {d.shadow_origin.observed_users} usuarios observados · Riesgo estimado {d.shadow_origin.estimated_risk}</p>
                </div>
              </CardContent>
            </Card>
          )}
          {d.reevaluations.length > 0 && (
            <Card className="shadow-card border-border">
              <CardContent className="p-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Reevaluaciones</p>
                {d.reevaluations.map((r) => (
                  <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 py-2 border-b border-border last:border-0 text-xs">
                    <span>{r.trigger} · {r.reason}</span>
                    <div className="flex items-center gap-2"><RiskBadge level={r.previous_risk} /> → <RiskBadge level={r.estimated_new_risk} /><StatusBadge status={r.status} /></div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          {d.history.length === 0 ? <EmptyState icon={History} title="Sin cambios registrados" subtitle="Los cambios de la ficha se registran con valores antes/después." /> : d.history.map((h) => (
            <Card key={h.id} className="shadow-card border-border">
              <CardContent className="p-4 text-xs">
                <div className="flex justify-between flex-wrap gap-2">
                  <p className="font-semibold">{h.user}</p>
                  <p className="text-muted-foreground">{fmtDateTime(h.timestamp)}</p>
                </div>
                {h.reason && <p className="text-muted-foreground mt-1">Motivo: {h.reason}</p>}
                <div className="grid sm:grid-cols-2 gap-3 mt-2">
                  <div className="rounded-lg bg-risk-high/5 border border-risk-high/20 p-2.5"><p className="font-semibold text-risk-high mb-1">Antes</p><pre className="whitespace-pre-wrap font-sans">{JSON.stringify(h.before, null, 1)}</pre></div>
                  <div className="rounded-lg bg-accent/5 border border-accent/20 p-2.5"><p className="font-semibold text-accent mb-1">Después</p><pre className="whitespace-pre-wrap font-sans">{JSON.stringify(h.after, null, 1)}</pre></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}

const EditDialog = ({ ini, catalogs, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  const [reason, setReason] = useState("");
  useEffect(() => {
    if (open) setF({ business_owner: ini.business_owner, stage: ini.stage, residual_risk: ini.residual_risk, next_action: ini.next_action, usage_volume: ini.usage_volume, model_version: ini.model_version });
  }, [open, ini]);
  const save = async () => {
    try {
      await api.put(`/initiatives/${ini.id}`, { data: f, reason: reason || "Actualización de ficha" });
      toast.success("Iniciativa actualizada · cambio registrado en historial");
      setOpen(false);
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };
  if (!catalogs) return null;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" className="border-border" data-testid="edit-initiative-btn"><Pencil className="h-4 w-4 mr-2" /> Editar</Button></DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display">Editar iniciativa</DialogTitle><DialogDescription>Cada cambio queda registrado con valores antes/después en el historial y la auditoría.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Business owner</Label><Input value={f.business_owner || ""} onChange={(e) => setF({ ...f, business_owner: e.target.value })} data-testid="edit-owner-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Versión del modelo</Label><Input value={f.model_version || ""} onChange={(e) => setF({ ...f, model_version: e.target.value })} /></div>
            <div className="space-y-1.5"><Label className="text-xs">Etapa</Label>
              <Select value={f.stage} onValueChange={(v) => setF({ ...f, stage: v })}>
                <SelectTrigger data-testid="edit-stage-select"><SelectValue /></SelectTrigger>
                <SelectContent>{catalogs.stages.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Riesgo residual</Label>
              <Select value={f.residual_risk} onValueChange={(v) => setF({ ...f, residual_risk: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{catalogs.risk_levels.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Próxima acción</Label><Input value={f.next_action || ""} onChange={(e) => setF({ ...f, next_action: e.target.value })} /></div>
          <div className="space-y-1.5"><Label className="text-xs">Motivo del cambio</Label><Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivo para el registro de auditoría" data-testid="edit-reason-input" /></div>
        </div>
        <DialogFooter><Button onClick={save} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="edit-save-btn">Guardar cambios</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ReevalDialog = ({ ini, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [trigger, setTrigger] = useState(REEVAL_TRIGGERS[1]);
  const [reason, setReason] = useState("");
  const save = async () => {
    if (!reason) { toast.error("Indique el motivo de la reevaluación"); return; }
    try {
      await api.post("/reevaluations", { initiative_id: ini.id, trigger, reason, priority: "Alta" });
      toast.success("Reevaluación activada · el riesgo anterior queda conservado");
      setOpen(false); setReason("");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" className="border-border" data-testid="trigger-reeval-btn"><RefreshCcw className="h-4 w-4 mr-2" /> Reevaluar</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display">Activar reevaluación</DialogTitle><DialogDescription>Se conservará el riesgo anterior ({ini.residual_risk}) y se generará una nueva versión de evaluación.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label className="text-xs">Motivo gatillante</Label>
            <Select value={trigger} onValueChange={setTrigger}>
              <SelectTrigger data-testid="reeval-trigger-select"><SelectValue /></SelectTrigger>
              <SelectContent>{REEVAL_TRIGGERS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Descripción del cambio</Label><Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Describa el cambio que obliga a reevaluar" data-testid="reeval-reason-input" /></div>
        </div>
        <DialogFooter><Button onClick={save} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="reeval-save-btn">Activar reevaluación</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AssignControlDialog = ({ id, catalogs, existing, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [controlId, setControlId] = useState("");
  const available = catalogs.controls.filter((c) => !existing.some((e) => e.control_id === c.id));
  const save = async () => {
    if (!controlId) return;
    try {
      await api.post(`/initiatives/${id}/controls`, { control_id: controlId });
      toast.success("Control asignado");
      setOpen(false); setControlId("");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="assign-control-btn"><Plus className="h-4 w-4 mr-1.5" /> Asignar control</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display">Asignar control del catálogo</DialogTitle></DialogHeader>
        <Select value={controlId} onValueChange={setControlId}>
          <SelectTrigger data-testid="control-select"><SelectValue placeholder="Seleccione un control" /></SelectTrigger>
          <SelectContent>{available.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.category}</SelectItem>)}</SelectContent>
        </Select>
        <DialogFooter><Button onClick={save} disabled={!controlId} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="control-save-btn">Asignar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AddDocumentDialog = ({ id, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [type, setType] = useState(DOC_TYPES[0]);
  const save = async () => {
    if (!title) { toast.error("Indique el título del documento"); return; }
    try {
      await api.post(`/initiatives/${id}/documents`, { title, type });
      toast.success("Evidencia adjuntada · registrada en auditoría");
      setOpen(false); setTitle("");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="add-document-btn"><Plus className="h-4 w-4 mr-1.5" /> Adjuntar evidencia</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display">Adjuntar documento</DialogTitle><DialogDescription>Metadatos del documento; el archivo físico se gestiona en el repositorio documental.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label className="text-xs">Título</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ej: DPIA v1.0" data-testid="document-title-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Tipo</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="document-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{DOC_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter><Button onClick={save} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="document-save-btn">Adjuntar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AddMetricDialog = ({ id, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ metric: METRIC_TYPES[0], value: "", unit: "", source: "", period: "2025-Q4", target: "" });
  const save = async () => {
    if (!f.value || !f.unit) { toast.error("Valor y unidad son obligatorios"); return; }
    try {
      await api.post(`/initiatives/${id}/metrics`, { ...f, value: Number(f.value), target: f.target ? Number(f.target) : null });
      toast.success("Métrica registrada");
      setOpen(false);
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="add-metric-btn"><Plus className="h-4 w-4 mr-1.5" /> Registrar métrica</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display">Registrar métrica de valor</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1.5"><Label className="text-xs">Métrica</Label>
            <Select value={f.metric} onValueChange={(v) => setF({ ...f, metric: v })}>
              <SelectTrigger data-testid="metric-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{METRIC_TYPES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Valor</Label><Input type="number" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} data-testid="metric-value-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Unidad</Label><Input value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} placeholder="hrs/semana, %, MM CLP" data-testid="metric-unit-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Fuente</Label><Input value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} /></div>
          <div className="space-y-1.5"><Label className="text-xs">Periodo</Label><Input value={f.period} onChange={(e) => setF({ ...f, period: e.target.value })} /></div>
          <div className="space-y-1.5"><Label className="text-xs">Meta</Label><Input type="number" value={f.target} onChange={(e) => setF({ ...f, target: e.target.value })} /></div>
        </div>
        <DialogFooter><Button onClick={save} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="metric-save-btn">Registrar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
